import { VendorLocationData, DeductionCategory, PayoutAdjustment } from "./types";
import { VendorDetail, Section } from "./VendorDetail";
import { DeductionsSection } from "./DeductionsSection";
import { AdjustmentsSection } from "./AdjustmentsSection";
import { PayoutSummary } from "./PayoutSummary";
import { usd } from "./helpers";

export interface VendorGroup {
  vendorId: number;
  vendorName: string;
  revenueShare: number;
  salesTaxResponsible: boolean;
  locations: VendorLocationData[];
  // Aggregated across all locations
  totalGross: number;
  totalTax: number;
  totalNetSales: number;
  totalCommission: number;
  totalVendorCommission: number;
  totalTips: number;
  // Vendor-level (not per-location)
  deductionCategories: DeductionCategory[];
  commissaryTotal: number;
  manualDeductions: PayoutAdjustment[];
  totalManualDeductions: number;
  totalDeductions: number;
  adjustments: PayoutAdjustment[];
  totalAdjustments: number;
  participationFee: number;
  totalDue: number;
}

interface VendorCardProps {
  group: VendorGroup;
  expandedLocationKey: string | null;
  onToggleLocation: (key: string) => void;
  onAddAdjustment: (vendorId: number, description: string, amountCents: number) => Promise<void>;
  onAddDeduction: (vendorId: number, description: string, amountCents: number) => Promise<void>;
  onDeleteAdjustment: (id: number) => Promise<void>;
}

export const VendorCard = ({
  group,
  expandedLocationKey,
  onToggleLocation,
  onAddAdjustment,
  onAddDeduction,
  onDeleteAdjustment,
}: VendorCardProps) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:border-red-500 transition-colors">
    {/* Vendor header */}
    <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
      <div>
        <div className="font-semibold text-gray-900 text-base">{group.vendorName}</div>
        <div className="text-xs text-gray-400">
          {group.locations.length} location{group.locations.length !== 1 ? "s" : ""}
          {" \u00b7 "}
          Commission: {group.revenueShare}%
          {" \u00b7 "}
          Sales tax: {group.salesTaxResponsible ? "Vendor remits" : "Organizer remits"}
        </div>
      </div>
      <div className="flex items-center gap-6 text-sm">
        <Stat label="Gross" value={usd(group.totalGross)} />
        <Stat label="Tax" value={usd(group.totalTax)} />
        <Stat
          label="Total Due"
          value={group.totalDue < 0 ? `-${usd(Math.abs(group.totalDue))}` : usd(group.totalDue)}
          color={group.totalDue >= 0 ? "text-green-700" : "text-red-700"}
          bold
        />
      </div>
    </div>

    {/* Location rows */}
    <div className="divide-y divide-gray-100">
      {group.locations.map((loc) => {
        const key = `${loc.vendorId}::${loc.locationId}`;
        const isExpanded = expandedLocationKey === key;

        return (
          <div key={key}>
            <button
              onClick={() => onToggleLocation(key)}
              className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <svg
                  className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-700 truncate">
                  {loc.locationName}
                </span>
              </div>
              <div className="flex items-center gap-5 text-xs flex-shrink-0">
                <Stat label="Gross" value={usd(loc.grossSales)} small />
                <Stat label="Tax" value={usd(loc.salesTax)} small />
                <Stat label="Commission" value={usd(loc.vendorCommission)} small />
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-gray-200 bg-gray-50/50">
                <VendorDetail data={loc} />
              </div>
            )}
          </div>
        );
      })}
    </div>

    {/* Vendor-level sections: Deductions, Adjustments, Payout */}
    <div className="border-t border-gray-300 bg-gray-50 px-4 py-4 space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section title="Deductions">
          <DeductionsSection
            categories={group.deductionCategories}
            commissaryTotal={group.commissaryTotal}
            manualDeductions={group.manualDeductions}
            totalManualDeductions={group.totalManualDeductions}
            totalDeductions={group.totalDeductions}
            onAdd={(desc, amount) => onAddDeduction(group.vendorId, desc, amount)}
            onDelete={onDeleteAdjustment}
          />
        </Section>

        <Section title="Adjustments">
          <AdjustmentsSection
            adjustments={group.adjustments}
            totalAdjustments={group.totalAdjustments}
            onAdd={(desc, amount) => onAddAdjustment(group.vendorId, desc, amount)}
            onDelete={onDeleteAdjustment}
          />
        </Section>

        <Section title="Payout">
          <PayoutSummary
            vendorCommission={group.totalVendorCommission}
            tips={group.totalTips}
            salesTax={group.totalTax}
            salesTaxResponsible={group.salesTaxResponsible}
            totalDeductions={group.totalDeductions}
            totalAdjustments={group.totalAdjustments}
            participationFee={group.participationFee}
            totalDue={group.totalDue}
          />
        </Section>
      </div>
    </div>
  </div>
);

const Stat = ({
  label,
  value,
  color,
  bold,
  small,
}: {
  label: string;
  value: string;
  color?: string;
  bold?: boolean;
  small?: boolean;
}) => (
  <div className="text-right">
    <div className={`text-gray-400 uppercase ${small ? "text-[9px]" : "text-[10px]"}`}>{label}</div>
    <div className={`tabular-nums ${color ?? "text-gray-700"} ${bold ? "font-semibold" : ""} ${small ? "text-xs" : ""}`}>
      {value}
    </div>
  </div>
);

/** Group flat VendorLocationData[] into VendorGroup[] */
export const groupByVendor = (data: VendorLocationData[]): VendorGroup[] => {
  const map: Record<number, VendorGroup> = {};

  data.forEach((loc) => {
    if (!map[loc.vendorId]) {
      // Take deductions/adjustments/fee from the first location (they're the same for all locations of a vendor)
      map[loc.vendorId] = {
        vendorId: loc.vendorId,
        vendorName: loc.vendorName,
        revenueShare: loc.revenueShare,
        salesTaxResponsible: loc.salesTaxResponsible,
        locations: [],
        totalGross: 0,
        totalTax: 0,
        totalNetSales: 0,
        totalCommission: 0,
        totalVendorCommission: 0,
        totalTips: 0,
        deductionCategories: loc.deductionCategories,
        commissaryTotal: loc.commissaryTotal,
        manualDeductions: loc.manualDeductions,
        totalManualDeductions: loc.totalManualDeductions,
        totalDeductions: loc.totalDeductions,
        adjustments: loc.adjustments,
        totalAdjustments: loc.totalAdjustments,
        participationFee: loc.participationFee,
        totalDue: 0, // calculated below
      };
    }
    const group = map[loc.vendorId];
    group.locations.push(loc);
    group.totalGross += loc.grossSales;
    group.totalTax += loc.salesTax;
    group.totalNetSales += loc.netSales;
    group.totalCommission += loc.commissionAmount;
    group.totalVendorCommission += loc.vendorCommission;
    group.totalTips += loc.tips;
  });

  // Calculate vendor-level totalDue. When the vendor is sales-tax-responsible
  // we pass the collected tax through to them so they can remit it.
  Object.values(map).forEach((group) => {
    const taxPassThrough = group.salesTaxResponsible ? group.totalTax : 0;
    group.totalDue =
      group.totalVendorCommission +
      group.totalTips +
      taxPassThrough +
      group.totalAdjustments -
      group.totalDeductions -
      group.participationFee;
  });

  return Object.values(map).sort((a, b) => (a.vendorName ?? "").localeCompare(b.vendorName ?? ""));
};
